import { getCachedUser } from '../hooks/useAuth';
/**
 * Workspace Service
 * =================
 * Handles all workspace operations with DUAL MODE support:
 * - DEMO MODE: Uses localStorage for persistence (no auth required)
 * - AUTH MODE: Uses Supabase for persistence (authenticated users)
 * 
 * The UX is identical in both modes - only persistence changes.
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';

// ============================================================================
// LOCAL STORAGE KEYS (DEMO MODE)
// ============================================================================

const STORAGE_KEYS = {
  SAVED_MATERIALS: 'sr_demo_saved_materials',
  PROJECTS: 'sr_demo_projects',
  MOODBOARDS: 'sr_demo_moodboards'
};

// ============================================================================
// P3: WORKSPACE LIMITS (DB-driven via get_user_feature_access RPC)
// ============================================================================

// Fallback limits (used only when Supabase is unavailable)
const FALLBACK_LIMITS = {
  demo: { max_projects: 5, max_moodboards: 2 },
  free: { max_projects: 5, max_moodboards: 2 },
  pro: { max_projects: 15, max_moodboards: 20 },
  enterprise: { max_projects: Infinity, max_moodboards: Infinity }
};

/**
 * Get user's feature limits from Supabase
 * @returns {Promise<{max_projects: number, max_moodboards: number, source: string}>}
 */
export const getUserLimits = async () => {
  const user = await getAuthenticatedUser();
  
  // Demo mode - use fallback limits
  if (!user) {
    console.log('📊 LIMITS SOURCE: FALLBACK (demo mode)');
    return { ...FALLBACK_LIMITS.demo, source: 'fallback' };
  }
  
  if (!isSupabaseConfigured || !supabase) {
    console.log('📊 LIMITS SOURCE: FALLBACK (Supabase not configured)');
    return { ...FALLBACK_LIMITS.free, source: 'fallback' };
  }
  
  try {
    const { data, error } = await supabase.rpc('get_user_feature_access', {
      p_user_id: user.id
    });
    
    if (error) {
      console.error('❌ get_user_feature_access error:', error);
      console.log('📊 LIMITS SOURCE: FALLBACK (RPC error)');
      return { ...FALLBACK_LIMITS.free, source: 'fallback' };
    }
    
    if (data) {
      const limits = {
        max_projects: parseInt(data.max_projects, 10) || FALLBACK_LIMITS.free.max_projects,
        max_moodboards: parseInt(data.max_moodboards, 10) || FALLBACK_LIMITS.free.max_moodboards,
        source: 'supabase'
      };
      console.log('📊 LIMITS SOURCE: SUPABASE ✓', limits);
      return limits;
    }
    
    console.log('📊 LIMITS SOURCE: FALLBACK (empty response)');
    return { ...FALLBACK_LIMITS.free, source: 'fallback' };
    
  } catch (err) {
    console.error('❌ getUserLimits error:', err);
    console.log('📊 LIMITS SOURCE: FALLBACK (exception)');
    return { ...FALLBACK_LIMITS.free, source: 'fallback' };
  }
};

/**
 * Check if user can create a new project
 * @returns {Promise<{allowed: boolean, current: number, limit: number, message?: string}>}
 */
export const canCreateProject = async () => {
  const user = await getAuthenticatedUser();
  const limits = await getUserLimits();
  
  let currentCount = 0;
  
  if (!user) {
    // Demo mode - count from localStorage
    const projects = getLocalData(STORAGE_KEYS.PROJECTS);
    currentCount = projects.length;
  } else {
    // Auth mode - count from Supabase
    try {
      const { count, error } = await supabase
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      
      if (!error) {
        currentCount = count || 0;
      }
    } catch (err) {
      console.error('❌ canCreateProject count error:', err);
    }
  }
  
  const allowed = currentCount < limits.max_projects;
  const result = {
    allowed,
    current: currentCount,
    limit: limits.max_projects,
    remaining: Math.max(0, limits.max_projects - currentCount)
  };
  
  if (!allowed) {
    result.message = `Hai raggiunto il limite di ${limits.max_projects} progetti. Passa a un piano superiore per crearne di più.`;
  }
  
  console.log('🔐 canCreateProject:', result);
  return result;
};

/**
 * Check if user can create a new moodboard
 * @returns {Promise<{allowed: boolean, current: number, limit: number, message?: string}>}
 */
export const canCreateMoodboard = async () => {
  const user = await getAuthenticatedUser();
  const limits = await getUserLimits();
  
  let currentCount = 0;
  
  if (!user) {
    // Demo mode - count from localStorage
    const moodboards = getLocalData(STORAGE_KEYS.MOODBOARDS);
    currentCount = moodboards.length;
  } else {
    // Auth mode - count from Supabase
    try {
      const { count, error } = await supabase
        .from('moodboards')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      
      if (!error) {
        currentCount = count || 0;
      }
    } catch (err) {
      console.error('❌ canCreateMoodboard count error:', err);
    }
  }
  
  const allowed = currentCount < limits.max_moodboards;
  const result = {
    allowed,
    current: currentCount,
    limit: limits.max_moodboards,
    remaining: Math.max(0, limits.max_moodboards - currentCount)
  };
  
  if (!allowed) {
    result.message = `Hai raggiunto il limite di ${limits.max_moodboards} moodboard. Passa a un piano superiore per crearne di più.`;
  }
  
  console.log('🔐 canCreateMoodboard:', result);
  return result;
};

// ============================================================================
// HELPER: Check if user is authenticated
// ============================================================================

const getAuthenticatedUser = async () => {
  if (!isSupabaseConfigured || !supabase) return null;
  
  try {
    const user = getCachedUser(); const data = { user };
    return data?.user || null;
  } catch {
    return null;
  }
};

// ============================================================================
// LOCAL STORAGE HELPERS (DEMO MODE)
// ============================================================================

const getLocalData = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const setLocalData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
};

const generateLocalId = () => {
  return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================================================
// SAVED MATERIALS (FAVORITES)
// ============================================================================

/**
 * Get all saved materials for current user (demo or authenticated)
 * @returns {Promise<Array>} Array of saved material objects
 */
export const getSavedMaterials = async () => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE: Use localStorage
  if (!user) {
    return getLocalData(STORAGE_KEYS.SAVED_MATERIALS);
  }
  
  // AUTH MODE: Use Supabase
  try {
    const { data, error } = await supabase
      .from('saved_materials')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('[WorkspaceService] Error getting saved materials:', err);
    return [];
  }
};

/**
 * Check if a material is saved
 * @param {string} materialId - Material ID
 * @returns {Promise<boolean>}
 */
export const isMaterialSaved = async (materialId) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const saved = getLocalData(STORAGE_KEYS.SAVED_MATERIALS);
    return saved.some(m => m.material_id === materialId);
  }
  
  // AUTH MODE
  try {
    const { data, error } = await supabase
      .from('saved_materials')
      .select('id')
      .eq('user_id', user.id)
      .eq('material_id', materialId)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return !!data;
  } catch (err) {
    console.error('[WorkspaceService] Error checking saved status:', err);
    return false;
  }
};

/**
 * Save a material (add to favorites)
 * @param {string} materialId - Material ID
 * @param {Object} materialData - Material metadata
 * @returns {Promise<Object>} Result with success status
 */
export const saveMaterial = async (materialId, materialData = {}) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE: Save to localStorage
  if (!user) {
    const saved = getLocalData(STORAGE_KEYS.SAVED_MATERIALS);
    
    // Check if already saved
    if (saved.some(m => m.material_id === materialId)) {
      return { success: false, error: 'Already saved' };
    }
    
    const newItem = {
      id: generateLocalId(),
      material_id: materialId,
      material_name: materialData.name || null,
      material_image: materialData.image || null,
      material_type: materialData.type || null,
      notes: materialData.notes || null,
      created_at: new Date().toISOString()
    };
    
    saved.unshift(newItem);
    setLocalData(STORAGE_KEYS.SAVED_MATERIALS, saved);
    
    return { success: true, data: newItem, mode: 'demo' };
  }
  
  // AUTH MODE: Save to Supabase
  try {
    const { data, error } = await supabase
      .from('saved_materials')
      .insert({
        user_id: user.id,
        material_id: materialId,
        material_name: materialData.name || null,
        material_image: materialData.image || null,
        material_type: materialData.type || null,
        notes: materialData.notes || null
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error saving material:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Unsave a material (remove from favorites)
 * @param {string} materialId - Material ID
 * @returns {Promise<Object>} Result with success status
 */
export const unsaveMaterial = async (materialId) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const saved = getLocalData(STORAGE_KEYS.SAVED_MATERIALS);
    const filtered = saved.filter(m => m.material_id !== materialId);
    setLocalData(STORAGE_KEYS.SAVED_MATERIALS, filtered);
    return { success: true, mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    const { error } = await supabase
      .from('saved_materials')
      .delete()
      .eq('user_id', user.id)
      .eq('material_id', materialId);
    
    if (error) throw error;
    return { success: true, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error unsaving material:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Toggle save status for a material
 * @param {string} materialId - Material ID
 * @param {Object} materialData - Material metadata
 * @returns {Promise<Object>} Result with new saved status
 */
export const toggleSaveMaterial = async (materialId, materialData = {}) => {
  const isSaved = await isMaterialSaved(materialId);
  
  if (isSaved) {
    const result = await unsaveMaterial(materialId);
    return { ...result, isSaved: false };
  } else {
    const result = await saveMaterial(materialId, materialData);
    return { ...result, isSaved: true };
  }
};

// ============================================================================
// PROJECTS
// ============================================================================

/**
 * Get all projects
 * @returns {Promise<Array>} Array of project objects
 */
export const getProjects = async () => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    return getLocalData(STORAGE_KEYS.PROJECTS);
  }
  
  // AUTH MODE
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_materials (
          id,
          material_id,
          material_name,
          material_image,
          created_at
        )
      `)
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('[WorkspaceService] Error getting projects:', err);
    return [];
  }
};

/**
 * Get a single project by ID
 * @param {string} projectId - Project ID
 * @returns {Promise<Object|null>}
 */
export const getProject = async (projectId) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const projects = getLocalData(STORAGE_KEYS.PROJECTS);
    return projects.find(p => p.id === projectId) || null;
  }
  
  // AUTH MODE
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_materials (
          id,
          material_id,
          material_name,
          material_image,
          material_type,
          notes,
          created_at
        )
      `)
      .eq('id', projectId)
      .eq('user_id', user.id)
      .single();
    
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('[WorkspaceService] Error getting project:', err);
    return null;
  }
};

/**
 * Create a new project (P3: WITH LIMIT ENFORCEMENT)
 * @param {Object} projectData - Project data (name, description, client)
 * @returns {Promise<Object>}
 */
export const createProject = async (projectData) => {
  // P3: Check limits before creation
  const limitCheck = await canCreateProject();
  if (!limitCheck.allowed) {
    console.warn('🚫 Project creation blocked - limit reached');
    return { 
      success: false, 
      error: limitCheck.message,
      limitReached: true,
      current: limitCheck.current,
      limit: limitCheck.limit
    };
  }
  
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const projects = getLocalData(STORAGE_KEYS.PROJECTS);
    
    const newProject = {
      id: generateLocalId(),
      name: projectData.name,
      description: projectData.description || null,
      client: projectData.client || null,
      status: 'active',
      project_materials: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    projects.unshift(newProject);
    setLocalData(STORAGE_KEYS.PROJECTS, projects);
    
    return { success: true, data: newProject, mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    const { data, error } = await supabase
      .from('projects')
      .insert({
        user_id: user.id,
        name: projectData.name,
        description: projectData.description || null,
        client: projectData.client || null,
        status: 'active'
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data: { ...data, project_materials: [] }, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error creating project:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Update a project
 * @param {string} projectId - Project ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>}
 */
export const updateProject = async (projectId, updates) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const projects = getLocalData(STORAGE_KEYS.PROJECTS);
    const index = projects.findIndex(p => p.id === projectId);
    
    if (index === -1) {
      return { success: false, error: 'Project not found' };
    }
    
    projects[index] = {
      ...projects[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    setLocalData(STORAGE_KEYS.PROJECTS, projects);
    return { success: true, data: projects[index], mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', projectId)
      .eq('user_id', user.id)
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error updating project:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Delete a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>}
 */
export const deleteProject = async (projectId) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const projects = getLocalData(STORAGE_KEYS.PROJECTS);
    const filtered = projects.filter(p => p.id !== projectId);
    setLocalData(STORAGE_KEYS.PROJECTS, filtered);
    return { success: true, mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    // Delete project materials first
    await supabase
      .from('project_materials')
      .delete()
      .eq('project_id', projectId);
    
    // Delete project
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId)
      .eq('user_id', user.id);
    
    if (error) throw error;
    return { success: true, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error deleting project:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Add material to project
 * @param {string} projectId - Project ID
 * @param {string} materialId - Material ID
 * @param {Object} materialData - Material metadata
 * @returns {Promise<Object>}
 */
export const addMaterialToProject = async (projectId, materialId, materialData = {}) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const projects = getLocalData(STORAGE_KEYS.PROJECTS);
    const index = projects.findIndex(p => p.id === projectId);
    
    if (index === -1) {
      return { success: false, error: 'Project not found' };
    }
    
    // Check if already exists
    if (projects[index].project_materials?.some(m => m.material_id === materialId)) {
      return { success: false, error: 'Material already in project' };
    }
    
    const newMaterial = {
      id: generateLocalId(),
      material_id: materialId,
      material_name: materialData.name || null,
      material_image: materialData.image || null,
      material_type: materialData.type || null,
      notes: materialData.notes || null,
      created_at: new Date().toISOString()
    };
    
    if (!projects[index].project_materials) {
      projects[index].project_materials = [];
    }
    projects[index].project_materials.push(newMaterial);
    projects[index].updated_at = new Date().toISOString();
    
    setLocalData(STORAGE_KEYS.PROJECTS, projects);
    return { success: true, data: newMaterial, mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    // Check if material already in project
    const { data: existing } = await supabase
      .from('project_materials')
      .select('id')
      .eq('project_id', projectId)
      .eq('material_id', materialId)
      .single();
    
    if (existing) {
      return { success: false, error: 'Material already in project' };
    }
    
    const { data, error } = await supabase
      .from('project_materials')
      .insert({
        project_id: projectId,
        material_id: materialId,
        material_name: materialData.name || null,
        material_image: materialData.image || null,
        material_type: materialData.type || null,
        notes: materialData.notes || null
      })
      .select()
      .single();
    
    if (error) throw error;
    
    // Update project timestamp
    await supabase
      .from('projects')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', projectId);
    
    return { success: true, data, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error adding material to project:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Remove material from project
 * @param {string} projectId - Project ID
 * @param {string} materialId - Material ID
 * @returns {Promise<Object>}
 */
export const removeMaterialFromProject = async (projectId, materialId) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const projects = getLocalData(STORAGE_KEYS.PROJECTS);
    const index = projects.findIndex(p => p.id === projectId);
    
    if (index === -1) {
      return { success: false, error: 'Project not found' };
    }
    
    projects[index].project_materials = (projects[index].project_materials || [])
      .filter(m => m.material_id !== materialId);
    projects[index].updated_at = new Date().toISOString();
    
    setLocalData(STORAGE_KEYS.PROJECTS, projects);
    return { success: true, mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    const { error } = await supabase
      .from('project_materials')
      .delete()
      .eq('project_id', projectId)
      .eq('material_id', materialId);
    
    if (error) throw error;
    
    await supabase
      .from('projects')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', projectId);
    
    return { success: true, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error removing material from project:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// MOODBOARDS
// ============================================================================

/**
 * Get all moodboards
 * @returns {Promise<Array>}
 */
export const getMoodboards = async () => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    return getLocalData(STORAGE_KEYS.MOODBOARDS);
  }
  
  // AUTH MODE
  try {
    const { data, error } = await supabase
      .from('moodboards')
      .select(`
        *,
        moodboard_items (
          id,
          material_id,
          material_name,
          material_image,
          position_x,
          position_y,
          width,
          height,
          created_at
        )
      `)
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('[WorkspaceService] Error getting moodboards:', err);
    return [];
  }
};

/**
 * Create a new moodboard (P3: WITH LIMIT ENFORCEMENT)
 * @param {Object} moodboardData - Moodboard data
 * @returns {Promise<Object>}
 */
export const createMoodboard = async (moodboardData) => {
  // P3: Check limits before creation
  const limitCheck = await canCreateMoodboard();
  if (!limitCheck.allowed) {
    console.warn('🚫 Moodboard creation blocked - limit reached');
    return { 
      success: false, 
      error: limitCheck.message,
      limitReached: true,
      current: limitCheck.current,
      limit: limitCheck.limit
    };
  }
  
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const moodboards = getLocalData(STORAGE_KEYS.MOODBOARDS);
    
    const newMoodboard = {
      id: generateLocalId(),
      name: moodboardData.name,
      description: moodboardData.description || null,
      moodboard_items: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    moodboards.unshift(newMoodboard);
    setLocalData(STORAGE_KEYS.MOODBOARDS, moodboards);
    
    return { success: true, data: newMoodboard, mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    const { data, error } = await supabase
      .from('moodboards')
      .insert({
        user_id: user.id,
        name: moodboardData.name,
        description: moodboardData.description || null
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data: { ...data, moodboard_items: [] }, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error creating moodboard:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Add material to moodboard
 * @param {string} moodboardId - Moodboard ID
 * @param {string} materialId - Material ID
 * @param {Object} materialData - Material metadata
 * @returns {Promise<Object>}
 */
export const addMaterialToMoodboard = async (moodboardId, materialId, materialData = {}) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const moodboards = getLocalData(STORAGE_KEYS.MOODBOARDS);
    const index = moodboards.findIndex(m => m.id === moodboardId);
    
    if (index === -1) {
      return { success: false, error: 'Moodboard not found' };
    }
    
    const newItem = {
      id: generateLocalId(),
      material_id: materialId,
      material_name: materialData.name || null,
      material_image: materialData.image || null,
      position_x: 0,
      position_y: 0,
      width: 200,
      height: 200,
      created_at: new Date().toISOString()
    };
    
    if (!moodboards[index].moodboard_items) {
      moodboards[index].moodboard_items = [];
    }
    moodboards[index].moodboard_items.push(newItem);
    moodboards[index].updated_at = new Date().toISOString();
    
    setLocalData(STORAGE_KEYS.MOODBOARDS, moodboards);
    return { success: true, data: newItem, mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    const { data, error } = await supabase
      .from('moodboard_items')
      .insert({
        moodboard_id: moodboardId,
        material_id: materialId,
        material_name: materialData.name || null,
        material_image: materialData.image || null,
        position_x: 0,
        position_y: 0,
        width: 200,
        height: 200
      })
      .select()
      .single();
    
    if (error) throw error;
    
    await supabase
      .from('moodboards')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', moodboardId);
    
    return { success: true, data, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error adding material to moodboard:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Delete a moodboard
 * @param {string} moodboardId - Moodboard ID
 * @returns {Promise<Object>}
 */
export const deleteMoodboard = async (moodboardId) => {
  const user = await getAuthenticatedUser();
  
  // DEMO MODE
  if (!user) {
    const moodboards = getLocalData(STORAGE_KEYS.MOODBOARDS);
    const filtered = moodboards.filter(m => m.id !== moodboardId);
    setLocalData(STORAGE_KEYS.MOODBOARDS, filtered);
    return { success: true, mode: 'demo' };
  }
  
  // AUTH MODE
  try {
    // Delete moodboard items first
    await supabase
      .from('moodboard_items')
      .delete()
      .eq('moodboard_id', moodboardId);
    
    // Delete moodboard
    const { error } = await supabase
      .from('moodboards')
      .delete()
      .eq('id', moodboardId)
      .eq('user_id', user.id);
    
    if (error) throw error;
    return { success: true, mode: 'auth' };
  } catch (err) {
    console.error('[WorkspaceService] Error deleting moodboard:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// UTILITY: Check current mode
// ============================================================================

export const getWorkspaceMode = async () => {
  const user = await getAuthenticatedUser();
  return user ? 'auth' : 'demo';
};

export default {
  // Saved materials
  getSavedMaterials,
  isMaterialSaved,
  saveMaterial,
  unsaveMaterial,
  toggleSaveMaterial,
  // Projects
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  addMaterialToProject,
  removeMaterialFromProject,
  // Moodboards
  getMoodboards,
  createMoodboard,
  addMaterialToMoodboard,
  deleteMoodboard,
  // P3: Limits
  getUserLimits,
  canCreateProject,
  canCreateMoodboard,
  // Utility
  getWorkspaceMode
};
